
import { supabase } from '@/integrations/supabase/client';

export interface AvailabilitySlot {
  id?: string;
  mentor_id?: string;
  day_of_week: number;
  start_time: string;
  end_time: string;
  is_active: boolean;
  created_at?: string;
  updated_at?: string;
  recurring?: boolean;
  timezone?: string;
}

export interface DateSpecificAvailability {
  id?: string;
  mentor_id: string;
  specific_date: string; // YYYY-MM-DD format
  start_time: string;
  end_time: string;
  is_active: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface BookingPreferences {
  session_duration: number;
  buffer_time: number;
  advance_booking_days: number;
  timezone: string;
  auto_approve_bookings: boolean;
}

export const createOrUpdateAvailability = async (mentorId: string, slots: AvailabilitySlot[]) => {
  try {
    // Delete existing availability
    await supabase
      .from('mentor_availability')
      .delete()
      .eq('mentor_id', mentorId);

    // Insert new availability slots
    const slotsToInsert = slots.map(slot => ({
      mentor_id: mentorId,
      day_of_week: slot.day_of_week,
      start_time: slot.start_time,
      end_time: slot.end_time,
      is_active: slot.is_active,
      recurring: slot.recurring || true,
      timezone: slot.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone
    }));

    const { error } = await supabase
      .from('mentor_availability')
      .insert(slotsToInsert);

    if (error) throw error;

    return { success: true };
  } catch (error) {
    console.error('Error updating availability:', error);
    return { success: false, error };
  }
};

export const createOrUpdateDateSpecificAvailability = async (
  mentorId: string, 
  date: string, 
  timeSlots: { start_time: string; end_time: string; is_active: boolean }[]
) => {
  try {
    // First, delete existing date-specific availability for this date
    await supabase
      .from('mentor_date_specific_availability')
      .delete()
      .eq('mentor_id', mentorId)
      .eq('specific_date', date);

    // Insert new date-specific availability
    const slotsToInsert = timeSlots.map(slot => ({
      mentor_id: mentorId,
      specific_date: date,
      start_time: slot.start_time,
      end_time: slot.end_time,
      is_active: slot.is_active
    }));

    const { error } = await supabase
      .from('mentor_date_specific_availability')
      .insert(slotsToInsert);

    if (error) throw error;

    return { success: true };
  } catch (error) {
    console.error('Error updating date-specific availability:', error);
    return { success: false, error };
  }
};

export const getDateSpecificAvailability = async (mentorId: string, date: string) => {
  try {
    const { data, error } = await supabase
      .from('mentor_date_specific_availability')
      .select('*')
      .eq('mentor_id', mentorId)
      .eq('specific_date', date);

    if (error) throw error;

    return { success: true, data: data || [] };
  } catch (error) {
    console.error('Error fetching date-specific availability:', error);
    return { success: false, error, data: [] };
  }
};

export const createOrUpdateBookingPreferences = async (
  mentorId: string, 
  preferences: BookingPreferences
) => {
  try {
    const { error } = await supabase
      .from('booking_preferences')
      .upsert({
        mentor_id: mentorId,
        ...preferences
      });

    if (error) throw error;

    return { success: true };
  } catch (error) {
    console.error('Error updating booking preferences:', error);
    return { success: false, error };
  }
};

export const getMentorAvailability = async (mentorId: string) => {
  try {
    const { data, error } = await supabase
      .from('mentor_availability')
      .select('*')
      .eq('mentor_id', mentorId)
      .order('day_of_week');

    if (error) throw error;

    return { success: true, data };
  } catch (error) {
    console.error('Error fetching availability:', error);
    return { success: false, error };
  }
};

export const getMentorBookingPreferences = async (mentorId: string) => {
  try {
    const { data, error } = await supabase
      .from('booking_preferences')
      .select('*')
      .eq('mentor_id', mentorId)
      .maybeSingle();

    if (error) throw error;

    return { success: true, data };
  } catch (error) {
    console.error('Error fetching booking preferences:', error);
    return { success: false, error };
  }
};
